import { createBrowserRouter } from 'react-router';
import { RouterErrorBoundary } from './components/router-error';
import { addBreadcrumb } from './lib/install-error-reporter';
import RootLayout from './routes/root';

// Rendered during the initial hydration tick before any loader resolves.
// Without this, React Router 7 logs "No `HydrateFallback` element provided".
function RouteHydrateFallback(): React.JSX.Element {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      className="mx-auto max-w-5xl px-6 py-10 text-[var(--color-ink-muted)]"
    >
      Loading…
    </div>
  );
}

// Data-mode router: every route pre-declares its loader so React Router
// can kick off data fetches before the component mounts. Lazy imports
// keep the initial bundle small. `errorElement` catches dynamic-import
// failures (stale chunk hashes after a redeploy) and triggers a single
// reload; anything else shows a readable fallback.
export const router = createBrowserRouter([
  {
    path: '/',
    Component: RootLayout,
    ErrorBoundary: RouterErrorBoundary,
    HydrateFallback: RouteHydrateFallback,
    children: [
      {
        index: true,
        ErrorBoundary: RouterErrorBoundary,
        lazy: async () => {
          const mod = await import('./routes/today');
          return { Component: mod.default, loader: mod.loader };
        },
      },
      {
        path: 'add',
        ErrorBoundary: RouterErrorBoundary,
        lazy: async () => {
          const mod = await import('./routes/add');
          return { Component: mod.default };
        },
      },
      {
        path: 'history',
        ErrorBoundary: RouterErrorBoundary,
        lazy: async () => {
          const mod = await import('./routes/history');
          return { Component: mod.default };
        },
      },
      {
        path: 'goals',
        ErrorBoundary: RouterErrorBoundary,
        lazy: async () => {
          const mod = await import('./routes/goals');
          return { Component: mod.default };
        },
      },
      {
        path: 'settings',
        ErrorBoundary: RouterErrorBoundary,
        lazy: async () => {
          const mod = await import('./routes/settings');
          return { Component: mod.default };
        },
      },
      {
        path: 'agents',
        ErrorBoundary: RouterErrorBoundary,
        lazy: async () => {
          const mod = await import('./routes/agents');
          return { Component: mod.default };
        },
      },
      {
        path: 'health-legacy',
        ErrorBoundary: RouterErrorBoundary,
        lazy: async () => {
          const mod = await import('./routes/health-legacy');
          return { Component: mod.default };
        },
      },
    ],
  },
]);

// Drop a breadcrumb on every settled navigation so the client-error
// reporter can show the user's last few routes alongside the crash.
// Subscribed once at module load — `createBrowserRouter` returns a
// singleton so duplicate listeners aren't a concern. Guarded with a
// typeof check so the unit-test stub (which only exposes `routes`) can
// still import this module without throwing.
if (typeof router.subscribe === 'function') {
  router.subscribe((state) => {
    if (state.navigation.state !== 'idle') return;
    const path = state.location.pathname;
    if (!path) return;
    addBreadcrumb('nav', path);
  });
}
